import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { useNovidades } from '../hooks/useNovidades';
import Tag from '../components/Tag';

const NovidadesPageContainer = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
`;

const Header = styled.header`
  background: #ba3b3b;
  color: #f0eadd;
  padding: 40px 0;
  text-align: center;
`;

const HeaderTitle = styled.h1`
  font-family: 'League Spartan', Arial, Helvetica, sans-serif;
  font-size: 48px;
  font-weight: bold;
  margin: 0 0 20px 0;
`;

const HeaderSubtitle = styled.p`
  font-family: 'Arial Nova', Arial, Helvetica, sans-serif;
  font-size: 18px;
  margin: 0;
  opacity: 0.9;
`;

const SearchContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 30px 20px 0;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  border-radius: 8px;
  padding: 6px 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 2px solid transparent;

  &:focus-within {
    box-shadow: 0 6px 25px rgba(186, 59, 59, 0.25);
    border-color: #ba3b3b;
    transform: translateY(-2px);
  }

  &:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
`;

const SearchIcon = styled.span`
  font-size: 20px;
  color: #ba3b3b;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;

  ${SearchBar}:focus-within & {
    transform: scale(1.1);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-family: 'Arial Nova', Arial, Helvetica, sans-serif;
  font-size: 16px;
  color: #333;
  background: transparent;
  min-width: 0;

  &::placeholder {
    color: #999;
    font-style: italic;
  }
`;

const ClearButton = styled.button`
  background: #ba3b3b;
  color: #f0eadd;
  border: none;
  border-radius: 20px;
  padding: 8px 18px;
  font-family: 'Arial Nova', Arial, Helvetica, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: #8b2a2a;
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(186, 59, 59, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const SearchResultsInfo = styled.p`
  text-align: center;
  font-family: 'Arial Nova', Arial, Helvetica, sans-serif;
  font-size: 16px;
  color: #666;
  margin-top: 15px;
  font-style: italic;
`;

const Content = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 20px;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const LoadingText = styled.p`
  font-family: 'Arial Nova', Arial, Helvetica, sans-serif;
  font-size: 24px;
  color: #333;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border-radius: 8px;
  margin: 20px 0;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
`;

const ErrorText = styled.p`
  font-family: 'Arial Nova', Arial, Helvetica, sans-serif;
  font-size: 18px;
  color: #d32f2f;
`;

const NovidadesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 40px;
  align-items: stretch;
`;

const NovidadeCard = styled.article`
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0,0,0,0.15);
  }
`;

const NovidadeHeader = styled.div`
  background: #ba3b3b;
  color: #f0eadd;
  padding: 20px 25px 25px 25px;
  flex-shrink: 0;
`;

const NovidadeTitle = styled.h2`
  font-family: 'League Spartan', Arial, Helvetica, sans-serif;
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  line-height: 1.2;
  text-align: center;
  min-height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NovidadeContent = styled.div`
  padding: 25px;
  line-height: 1.6;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const NovidadeText = styled.div`
  font-family: 'Arial Nova', Arial, Helvetica, sans-serif;
  font-size: 16px;
  color: #333;
  line-height: 1.7;
  max-height: 200px;
  overflow: hidden;
  position: relative;
  flex: 1;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(transparent, rgba(255, 255, 255, 0.8), #fff);
    pointer-events: none;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: 'League Spartan', Arial, Helvetica, sans-serif;
    color: #ba3b3b;
    margin: 20px 0 10px 0;
  }

  h1 { font-size: 24px; }
  h2 { font-size: 22px; }
  h3 { font-size: 20px; }
  h4 { font-size: 18px; }
  h5 { font-size: 16px; }
  h6 { font-size: 14px; }

  p {
    margin: 0 0 15px 0;
  }

  ul, ol {
    margin: 15px 0;
    padding-left: 20px;
  }

  li {
    margin: 5px 0;
  }

  strong, b {
    font-weight: bold;
    color: #ba3b3b;
  }

  em, i {
    font-style: italic;
  }

  blockquote {
    border-left: 4px solid #ba3b3b;
    padding-left: 20px;
    margin: 20px 0;
    font-style: italic;
    color: #666;
  }

  a {
    color: #ba3b3b;
    text-decoration: underline;
    
    &:hover {
      color: #8b2a2a;
    }
  }

  &.expanded {
    max-height: none;
    
    &::after {
      display: none;
    }
  }
`;

const ReadMoreButton = styled.button`
  background: transparent;
  color: #ba3b3b;
  font-family: 'Arial Nova', Arial, Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 500;
  border: none;
  padding: 8px 0;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;
  position: relative;
  z-index: 1;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: #8b2a2a;
    text-decoration-thickness: 2px;
  }
`;

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const DialogContent = styled.div`
  background: #fff;
  border-radius: 12px;
  max-width: 800px;
  width: 100%;
  max-height: 80vh;
  position: relative;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
`;

const DialogHeader = styled.div`
  background: #ba3b3b;
  color: #f0eadd;
  padding: 25px;
  border-radius: 12px 12px 0 0;
  flex-shrink: 0;
  z-index: 10;
`;

const DialogTitle = styled.h2`
  font-family: 'League Spartan', Arial, Helvetica, sans-serif;
  font-size: 28px;
  font-weight: bold;
  margin: 0 0 10px 0;
  line-height: 1.2;
`;

const DialogBody = styled.div`
  padding: 30px;
  overflow-y: auto;
  flex: 1;
`;

const DialogText = styled.div`
  font-family: 'Arial Nova', Arial, Helvetica, sans-serif;
  font-size: 16px;
  color: #333;
  line-height: 1.7;

  h1, h2, h3, h4, h5, h6 {
    font-family: 'League Spartan', Arial, Helvetica, sans-serif;
    color: #ba3b3b;
    margin: 20px 0 10px 0;
  }

  h1 { font-size: 24px; }
  h2 { font-size: 22px; }
  h3 { font-size: 20px; }
  h4 { font-size: 18px; }
  h5 { font-size: 16px; }
  h6 { font-size: 14px; }

  p {
    margin: 0 0 15px 0;
  }

  ul, ol {
    margin: 15px 0;
    padding-left: 20px;
  }

  li {
    margin: 5px 0;
  }

  strong, b {
    font-weight: bold;
    color: #ba3b3b;
  }

  em, i {
    font-style: italic;
  }

  blockquote {
    border-left: 4px solid #ba3b3b;
    padding-left: 20px;
    margin: 20px 0;
    font-style: italic;
    color: #666;
  }

  a {
    color: #ba3b3b;
    text-decoration: underline;
    
    &:hover {
      color: #8b2a2a;
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: -20px;
  right: -20px;
  background: #222;
  color: #f0eadd;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: #444;
  }
`;

const DialogFooter = styled.div`
  background: #f8f8f8;
  border-top: 1px solid #e0e0e0;
  padding: 20px 25px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 15px;
  min-height: 80px;
  box-sizing: border-box;
  border-radius: 0 0 12px 12px;
  flex-shrink: 0;
`;

const NovidadeFooter = styled.div`
  background: #f8f8f8;
  border-top: 1px solid #e0e0e0;
  padding: 20px 25px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 15px;
  height: 80px;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const WriterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 100%;
`;

const ClickableWriterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 100%;
  cursor: pointer;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const ClickableWriterAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #ba3b3b;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const ClickableWriterName = styled.span`
  font-family: 'League Spartan', Arial, Helvetica, sans-serif;
  font-size: 16px;
  font-weight: bold;
  color: #ba3b3b;
  line-height: 1.2;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: #8b2a2a;
  }
`;

const TakeLabel = styled.span`
  font-family: 'Arial Nova', Arial, Helvetica, sans-serif;
  font-size: 10px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
`;

const WriterDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;

const WriterLabel = styled.span`
  font-family: 'Arial Nova', Arial, Helvetica, sans-serif;
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const WriterName = styled.span`
  font-family: 'League Spartan', Arial, Helvetica, sans-serif;
  font-size: 16px;
  font-weight: bold;
  color: #ba3b3b;
  line-height: 1.2;
`;

const NovidadeDate = styled.div`
  font-family: 'Arial Nova', Arial, Helvetica, sans-serif;
  color: #666;
  text-align: right;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  gap: 2px;
`;

const DateMain = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

const DateYear = styled.span`
  font-size: 12px;
  opacity: 0.7;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
`;

const EmptyText = styled.p`
  font-family: 'Arial Nova', Arial, Helvetica, sans-serif;
  font-size: 20px;
  color: #666;
  margin: 0;
`;

const NovidadesPage = () => {
  const { data, error, loading } = useNovidades();
  const [selectedNovidade, setSelectedNovidade] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!searchTerm.trim()) {
      return;
    }

    const timer = setTimeout(() => {
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const novidadeId = searchParams.get('id');
    
    if (novidadeId && data && data.length > 0) {
      const novidade = data.find(novidade => 
        novidade.id && novidade.id.toString() === novidadeId
      );
      
      if (novidade) {
        setSelectedNovidade(novidade);
        setIsDialogOpen(true);
      }
    }
  }, [searchParams, data]);

  const renderHTML = (htmlContent) => {
    if (!htmlContent) return '';

    const sanitizedHTML = htmlContent
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '');

    return { __html: sanitizedHTML };
  };

  const formatDate = (dateString) => {
    if (!dateString) return { main: '', year: '' };
    const date = new Date(dateString);
    const main = date.toLocaleDateString('pt-BR', {
      month: 'long',
      day: 'numeric'
    });
    const year = date.getFullYear().toString();
    return { main, year };
  };

  const openDialog = (novidade) => {
    setSelectedNovidade(novidade);
    setIsDialogOpen(true);
    setSearchParams({ id: novidade.id });
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setSelectedNovidade(null);
    setSearchParams({});
  };

  const normalizeText = (text) => {
    if (!text) return '';
    
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, '');
  };

  const needsTruncation = (text) => {
    if (!text) return false;
    const textLength = text.replace(/<[^>]*>/g, '').length;
    return textLength > 300;
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const filteredData = data ? data.filter(novidade => {
    if (!searchTerm.trim()) return true;
    
    const searchNormalized = normalizeText(searchTerm);
    const title = normalizeText(novidade.title);
    const text = normalizeText(novidade.text ? novidade.text.replace(/<[^>]*>/g, '') : '');
    
    return title.includes(searchNormalized) || text.includes(searchNormalized);
  }) : [];

  return (
    <NovidadesPageContainer>
      <Header>
        <HeaderTitle>Novidades</HeaderTitle>
        <HeaderSubtitle>Últimas atualizações e novidades sobre o mundo do entretenimento</HeaderSubtitle>
        <SearchContainer>
          <SearchBar>
            <SearchIcon>🔍</SearchIcon>
            <SearchInput
              type="text"
              placeholder="Encontre o que está procurando"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <ClearButton onClick={handleClearSearch}>
                ✕ Limpar
              </ClearButton>
            )}
          </SearchBar>
          {searchTerm.trim() && (
            <SearchResultsInfo>
              {filteredData.length === 0
                ? `Nenhum resultado encontrado para "${searchTerm}"`
                : `${filteredData.length} ${filteredData.length === 1 ? 'resultado encontrado' : 'resultados encontrados'}`
              }
            </SearchResultsInfo>
          )}
        </SearchContainer>
      </Header>
      <Content>
        {loading ? (
          <LoadingContainer>
            <LoadingText>Carregando novidades...</LoadingText>
          </LoadingContainer>
        ) : error ? (
          <ErrorContainer>
            <ErrorText>Erro ao carregar as novidades. Tente novamente mais tarde.</ErrorText>
          </ErrorContainer>
        ) : !data || data.length === 0 ? (
          <EmptyState>
            <EmptyText>Nenhuma novidade encontrada no momento.</EmptyText>
          </EmptyState>
        ) : filteredData.length === 0 ? (
          <EmptyState>
            <EmptyText>Nenhuma novidade encontrada para a busca "{searchTerm}".</EmptyText>
          </EmptyState>
        ) : (
          <NovidadesGrid>
            {filteredData.map((novidade, index) => (
              <NovidadeCard key={novidade.id || index}>
                <NovidadeHeader>
                  <NovidadeTitle>{novidade.title || 'Novidade sem título'}</NovidadeTitle>
                </NovidadeHeader>
                <NovidadeContent>
                  <NovidadeText
                    dangerouslySetInnerHTML={renderHTML(novidade.text)}
                  />
                  {needsTruncation(novidade.text) && (
                    <ReadMoreButton onClick={() => openDialog(novidade)}>
                      Ler mais
                    </ReadMoreButton>
                  )}
                </NovidadeContent>
                <NovidadeFooter>
                  {novidade.writers ? (
                    <ClickableWriterInfo>
                      {novidade.writers.photo_url && (
                        <ClickableWriterAvatar
                          src={novidade.writers.photo_url}
                          alt={novidade.writers.name || 'Avatar do autor'}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <WriterDetails>
                        <WriterLabel>Por</WriterLabel>
                        <ClickableWriterName>
                          {novidade.writers ? novidade.writers.name : 'Autor desconhecido'}
                        </ClickableWriterName>
                        <TakeLabel>
                          {novidade.writers && novidade.writers.take ? 'Equipe Takes' : 'Representante Takes'}
                        </TakeLabel>
                        {novidade.tags && novidade.tags.name && (
                          <Tag>{novidade.tags.name}</Tag>
                        )}
                      </WriterDetails>
                    </ClickableWriterInfo>
                  ) : (
                    <WriterInfo>
                      <WriterDetails>
                        <WriterLabel>Fonte</WriterLabel>
                        <WriterName>
                          {novidade.fonts ? novidade.fonts.name : 'Fonte desconhecida'}
                        </WriterName>
                      </WriterDetails>
                    </WriterInfo>
                  )}
                  <NovidadeDate>
                    {novidade.created_at && (() => {
                      const dateInfo = formatDate(novidade.created_at);
                      return (
                        <>
                          <DateMain>{dateInfo.main}</DateMain>
                          <DateYear>{dateInfo.year}</DateYear>
                        </>
                      );
                    })()}
                  </NovidadeDate>
                </NovidadeFooter>
              </NovidadeCard>
            ))}
          </NovidadesGrid>
        )}
      </Content>

      {isDialogOpen && selectedNovidade && (
        <DialogOverlay onClick={closeDialog}>
          <DialogContent onClick={(e) => e.stopPropagation()}>
            <DialogHeader>
              <DialogTitle>{selectedNovidade.title || 'Novidade sem título'}</DialogTitle>
              <CloseButton onClick={closeDialog}>×</CloseButton>
            </DialogHeader>
            <DialogBody>
              <DialogText
                dangerouslySetInnerHTML={renderHTML(selectedNovidade.text)}
              />
            </DialogBody>
            <DialogFooter>
              {selectedNovidade.writers ? (
                <ClickableWriterInfo>
                  {selectedNovidade.writers.photo_url && (
                    <ClickableWriterAvatar
                      src={selectedNovidade.writers.photo_url}
                      alt={selectedNovidade.writers.name || 'Avatar do autor'}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <WriterDetails>
                    <WriterLabel>Por</WriterLabel>
                    <ClickableWriterName>
                      {selectedNovidade.writers ? selectedNovidade.writers.name : 'Autor desconhecido'}
                    </ClickableWriterName>
                    <TakeLabel>
                      {selectedNovidade.writers && selectedNovidade.writers.take ? 'Equipe Takes' : 'Representante Takes'}
                    </TakeLabel>
                    {selectedNovidade.tags && selectedNovidade.tags.name && (
                      <Tag>{selectedNovidade.tags.name}</Tag>
                    )}
                  </WriterDetails>
                </ClickableWriterInfo>
              ) : (
                <WriterInfo>
                  <WriterDetails>
                    <WriterLabel>Fonte</WriterLabel>
                    <WriterName>
                      {selectedNovidade.fonts ? selectedNovidade.fonts.name : 'Fonte desconhecida'}
                    </WriterName>
                  </WriterDetails>
                </WriterInfo>
              )}
              <NovidadeDate>
                {selectedNovidade.created_at && (() => {
                  const dateInfo = formatDate(selectedNovidade.created_at);
                  return (
                    <>
                      <DateMain>{dateInfo.main}</DateMain>
                      <DateYear>{dateInfo.year}</DateYear>
                    </>
                  );
                })()}
              </NovidadeDate>
            </DialogFooter>
          </DialogContent>
        </DialogOverlay>
      )}
    </NovidadesPageContainer>
  );
};

export default NovidadesPage;